import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import AccordionBody from '../../../generic/Accordion/AccordionBody';
import useAccordion from '../../../generic/Accordion/useAccordion';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../generic/Button/button-constants';
import TooltipButton from '../../../generic/Button/TooltipButton';
import { Icon, icons } from '../../../generic/Icon';

import { FossabotFixOptionCard, FossabotSectionLabel } from './FossabotFixOptionCard';
import { useFossabotFixDisplay } from './useFossabotFixDisplay';
import { useFossabotFixOptions } from './useFossabotFixOptions';

import styles from './FossabotOtherFixesBox.module.less';

/**
 * The collapsible "Additional Remediation Options" card shown below the
 * FossabotFixBox in project scope. Lists every fix version other than the one
 * on the card, each as a radio with the first one selected by default, and a
 * Create button that opens a fossabot PR for the selected version. Renders
 * nothing when the card's fix is the only one.
 */
type Props = {
  /** Called with the selected fix version. */
  onCreatePR: (version: string) => void;
  /** When set, Create is disabled and this text explains why in a tooltip. */
  disabledReason?: string;
};

export const FossabotOtherFixesBox: VoidFunctionComponent<Props> = ({ onCreatePR, disabledReason }) => {
  const { options, cve } = useFossabotFixOptions();
  const { version: cardVersion } = useFossabotFixDisplay();
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
  const { isOpen, targetProps, bodyProps } = useAccordion({ id: 'fossabot-other-fixes', isInitiallyOpen: false });

  const otherOptions = options.filter((option) => option.version !== cardVersion);
  /*
    Defaults to the first option so Create works without a click. Resolved from
    the current options rather than trusted on its own: the box keeps its
    selection across an issue change, so a version selected earlier can vanish
    from the list. Reading it back swaps the stale one for the first option.
  */
  const selectedOption = otherOptions.find((option) => option.version === selectedVersion)
    ?? otherOptions[0]
    ?? null;

  if (otherOptions.length === 0) {
    return null;
  }

  return (
    <div className={styles.card} data-testid="fossabot-other-fixes-box">
      <button
        id={targetProps.id}
        type="button"
        className={styles.target}
        onClick={targetProps.onClick}
        aria-controls={targetProps['aria-controls']}
        aria-expanded={targetProps['aria-expanded']}
      >
        Additional Remediation Options
        <Icon icon={isOpen ? icons.angleUp : icons.angleDown} />
      </button>

      <AccordionBody
        isOpen={isOpen}
        id={bodyProps.id}
        role={bodyProps.role}
        aria-labelledby={bodyProps['aria-labelledby']}
        className={styles.body}
      >
        {/*
          These are siblings rather than one wrapped node on purpose: AccordionBody
          clones a lone child element to hand it `isOpen`, and React rejects that
          prop on a plain DOM node.
        */}
        <div className={styles.group}>
          <FossabotSectionLabel>FOSSABOT PR</FossabotSectionLabel>
          {otherOptions.map((option) => (
            <FossabotFixOptionCard
              key={option.version}
              option={option}
              cve={cve}
              value={option.version}
              isSelected={selectedOption?.version === option.version}
              onSelect={() => setSelectedVersion(option.version)}
            />
          ))}
        </div>
        <div className={styles.actions}>
          <TooltipButton
            id="fossabot-other-fixes-create-tooltip"
            tooltip={disabledReason}
            showTooltip={disabledReason !== undefined}
            isDisabled={disabledReason !== undefined || selectedOption === null}
            type={BUTTON_TYPE.SECONDARY}
            size={BUTTON_SIZE.MEDIUM}
            onClick={() => selectedOption !== null && onCreatePR(selectedOption.version)}
          >
            Create PR
          </TooltipButton>
        </div>
      </AccordionBody>
    </div>
  );
};
